import React, { useState } from "react";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { useApiContext } from "../../../api/context";
import { useErrorSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { consoleErrorWithSentry } from "../../../utils";
import { useFullScreen } from "../../../utils/dialog";
import { useInsightsContext } from "../context";

const NOT_RELAVANT = "not relevant";
const NOT_ENOUGH_INFORMATION = "not enough information";
const NOT_WORTH_THE_EFFORT = "not worth the effort";
const INACCURATE_OPTIMIZATION_OPPORTUNITIES = "inaccurate optimization opportunities";

type Props = {
  onClose: () => void;
};
const DismissInsightModal: React.FC<Props> = ({ onClose }) => {
  const { selectedInsight, setSelectedInsight } = useInsightsContext();

  const theme = useTheme();
  const { isMobile: isSmallScreen } = useFullScreen("sm");
  const { userId } = useAuthContext();
  const { customer } = useCustomerContext();
  const api = useApiContext();

  const showErrorSnackbar = useErrorSnackbar();

  const [reasons, setReasons] = useState({
    [NOT_RELAVANT]: false,
    [NOT_ENOUGH_INFORMATION]: false,
    [NOT_WORTH_THE_EFFORT]: false,
    [INACCURATE_OPTIMIZATION_OPPORTUNITIES]: false,
  });
  const [comment, setComment] = useState("");

  const handleDismiss = async () => {
    onClose();

    const data = {
      customerId: customer.id,
      providerId: selectedInsight?.providerId,
      key: selectedInsight?.key,
      status: "dismissed",
      userId,
      dismissalDetails: {
        reasons: Object.entries(reasons)
          .filter(([_, value]) => value)
          .map(([key]) => key),
        comment,
      },
    };

    try {
      await api.post("insights/status", data);

      setSelectedInsight((insight) => {
        if (!insight) {
          return insight;
        }

        return {
          ...insight,
          userStatusChanges: insight.userStatusChanges && {
            ...insight.userStatusChanges,
            status: "dismissed",
          },
        };
      });
    } catch (e) {
      consoleErrorWithSentry(e);
      showErrorSnackbar("Something went wrong. Please try again later.");
    }
  };

  return (
    <Dialog open={true} fullScreen={isSmallScreen} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Dismiss</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please select the reason(s) why you’re dismissing this insight. Mark all that apply:
        </Typography>
        <Typography sx={{ my: 0.5 }}>
          <Checkbox
            checked={reasons[0]}
            onChange={() => {
              setReasons((prevState) => ({
                ...prevState,
                "not relevant": true,
              }));
            }}
          />
          Not relevant
        </Typography>
        <Typography sx={{ my: 0.5 }}>
          <Checkbox
            checked={reasons[1]}
            onChange={() => {
              setReasons((prevState) => ({
                ...prevState,
                "not enough information": true,
              }));
            }}
          />
          Not enough information
        </Typography>
        <Typography sx={{ my: 0.5 }}>
          <Checkbox
            checked={reasons[2]}
            onChange={() => {
              setReasons((prevState) => ({
                ...prevState,
                "not worth the effort": true,
              }));
            }}
          />
          Not worth the effort
        </Typography>
        <Typography sx={{ my: 0.5 }}>
          <Checkbox
            checked={reasons[3]}
            onChange={() => {
              setReasons((prevState) => ({
                ...prevState,
                "inaccurate optimization opportunities": true,
              }));
            }}
          />
          Inaccurate optimization opportunities
        </Typography>
        <TextField
          label="Additional comments (optional)"
          variant="outlined"
          key="description"
          fullWidth
          multiline
          minRows={5}
          sx={{ mt: 2, mb: 1 }}
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
          }}
        />
        <Typography variant="caption" sx={{ mb: 2, color: theme.palette.text.secondary }}>
          * After dismissing it, the insight will be moved to the list of dismissed insights, in case you need to revert
          this action.
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="text" onClick={onClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button variant="contained" disabled={Object.values(reasons).every((r) => !r)} onClick={handleDismiss}>
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DismissInsightModal;
